"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button, Alert } from "reactstrap"
import { toast } from "react-toastify"
import { StaffCarService } from "../../../../Service/StaffCarService"
import type { Car, TokenData } from "./types"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
  initialData: Car | null
  token: string
  tokenData: TokenData
}

const CarModal: React.FC<Props> = ({ isOpen, toggle, onSuccess, initialData, token, tokenData }) => {
  const [formData, setFormData] = useState({
    plate_number: "",
    driver_name: "",
    driver_phone: "",
    status: true,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isEditing = !!initialData

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          plate_number: initialData.plate_number,
          driver_name: initialData.driver_name,
          driver_phone: initialData.driver_phone,
          status: initialData.status,
        })
      } else {
        setFormData({
          plate_number: "",
          driver_name: "",
          driver_phone: "",
          status: true,
        })
      }
      setError(null)
    }
  }, [isOpen, initialData])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      if (isEditing && initialData) {
        await StaffCarService.updateCar(token, initialData.id, formData)
        toast.success("Car updated successfully")
      } else {
        await StaffCarService.createCar(token, formData)
        toast.success("Car registered successfully")
      }

      onSuccess()
    } catch (error: any) {
      console.error("Failed to save car:", error)
      const errorMessage = error.response?.data?.message || error.message || "Failed to save car"
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>{isEditing ? "Edit Car" : "Register Car"}</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {error && (
            <Alert color="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <FormGroup>
            <Label for="plate_number">Plate Number *</Label>
            <Input
              type="text"
              name="plate_number"
              id="plate_number"
              value={formData.plate_number}
              onChange={handleInputChange}
              required
              disabled={loading}
              placeholder="Enter plate number"
              style={{ textTransform: "uppercase" }}
            />
          </FormGroup>

          <FormGroup>
            <Label for="driver_name">Driver Name *</Label>
            <Input
              type="text"
              name="driver_name"
              id="driver_name"
              value={formData.driver_name}
              onChange={handleInputChange}
              required
              disabled={loading}
              placeholder="Enter driver's full name"
            />
          </FormGroup>

          <FormGroup>
            <Label for="driver_phone">Driver Phone *</Label>
            <Input
              type="tel"
              name="driver_phone"
              id="driver_phone"
              value={formData.driver_phone}
              onChange={handleInputChange}
              required
              disabled={loading}
              placeholder="Enter driver's phone number"
            />
          </FormGroup>

          <FormGroup check>
            <Label check>
              <Input
                type="checkbox"
                name="status"
                checked={formData.status}
                onChange={handleInputChange}
                disabled={loading}
              />
              Active Status
            </Label>
            <small className="form-text text-muted">Check if this car is currently active for the exhibition</small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={loading}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {isEditing ? "Updating..." : "Registering..."}
              </>
            ) : isEditing ? (
              "Update Car"
            ) : (
              "Register Car"
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default CarModal
